/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import React from 'react';
import PropTypes from 'prop-types';
import {
  Check,
  Shield,
  User,
  Key,
  Building2,
  Layers,
  Camera,
  Bot,
  Volume2,
  Palette,
  FileJson,
  PhoneCall,
  ClipboardList,
  BookOpen,
  ListChecks
} from 'lucide-react';
import { ACCOUNT_LABEL, SUB_ACCOUNT_LABEL, SERVICE_REQUEST_LABEL } from '../constants/entityLabels';

const ENTITY_CONFIG = {
  accounts: { label: `${ACCOUNT_LABEL.singular} Management`, icon: Building2, color: 'text-blue-500' },
  sub_accounts: { label: SUB_ACCOUNT_LABEL.plural, icon: Layers, color: 'text-cyan-500' },
  users: { label: 'User Management', icon: User, color: 'text-purple-500' },
  roles: { label: 'Roles & Permissions', icon: Shield, color: 'text-orange-500' },
  permissions: { label: 'Permissions', icon: Key, color: 'text-amber-500' },
  service_requests: { label: SERVICE_REQUEST_LABEL.plural, icon: ClipboardList, color: 'text-primary' },
  // placeAfter: render this card right after the named group instead of in key order.
  // actionOrder: list these actions in this order (others follow in key order).
  checklists: {
    label: `${SERVICE_REQUEST_LABEL.singular} Checklists`,
    icon: ListChecks,
    color: 'text-success',
    placeAfter: 'service_requests',
    actionOrder: [
      'create', 'rename', 'delete',
      'item_add', 'item_edit', 'item_status', 'item_reorder', 'item_delete',
      'comment_add', 'comment_edit', 'comment_delete',
    ],
  },
  media: { label: 'Media Access', icon: Camera, color: 'text-indigo-500' },
  audio: { label: 'Audio Access', icon: Volume2, color: 'text-rose-500' },
  ai: { label: 'AI Assistant', icon: Bot, color: 'text-violet-500' },
  themes: { label: 'Theme Builder', icon: Palette, color: 'text-fuchsia-500' },
  bulk: { label: 'Bulk Operations', icon: FileJson, color: 'text-emerald-500' },
  agents: { label: 'Agent Tools', icon: PhoneCall, color: 'text-amber-500' },
  knowledge_bases: { label: 'Knowledge Bases', icon: BookOpen, color: 'text-teal-500' },
  ai_agents: { label: 'AI Agents', icon: Bot, color: 'text-violet-500' },
};

const PermissionGrid = ({ permissions, selectedIds, onChange, isLoading }) => {
  // Group permissions by the prefix before the first dot (e.g., "assignments.view" → "assignments")
  const grouped = permissions.reduce((acc, p) => {
    const dotIndex = p.key.indexOf('.');
    const groupName = dotIndex !== -1 ? p.key.slice(0, dotIndex).toLowerCase() : 'settings';

    if (!acc[groupName]) acc[groupName] = [];
    acc[groupName].push(p);
    return acc;
  }, {});

  // Groups with an actionOrder list their actions in that order instead of key order.
  Object.entries(grouped).forEach(([entity, groupPermissions]) => {
    const actionOrder = ENTITY_CONFIG[entity]?.actionOrder;
    if (!actionOrder) return;
    const rank = (p) => {
      const index = actionOrder.indexOf(p.key.slice(p.key.indexOf('.') + 1));
      return index === -1 ? actionOrder.length : index;
    };
    groupPermissions.sort((a, b) => rank(a) - rank(b));
  });

  // Groups arrive in key order; move each group with placeAfter next to its related group.
  const orderedGroups = Object.entries(grouped);
  orderedGroups.forEach(([entity]) => {
    const anchor = ENTITY_CONFIG[entity]?.placeAfter;
    if (!anchor || !grouped[anchor]) return;
    const from = orderedGroups.findIndex(([name]) => name === entity);
    const [moved] = orderedGroups.splice(from, 1);
    const to = orderedGroups.findIndex(([name]) => name === anchor) + 1;
    orderedGroups.splice(to, 0, moved);
  });

  const togglePermission = (id) => {
    const perm = permissions.find(p => p.id === id);
    if (!perm) return;

    const isSelecting = !selectedIds.includes(id);
    let newSelected;

    if (isSelecting) {
      newSelected = [...selectedIds, id];

      // If selecting a 'manage' action, auto-select all in the same group
      const action = perm.key.split('.')[1]?.toLowerCase();
      if (action === 'manage') {
        const groupName = perm.key.split('.')[0]?.toLowerCase();
        if (groupName && grouped[groupName]) {
          const groupIds = grouped[groupName].map(p => p.id);
          newSelected = Array.from(new Set([...newSelected, ...groupIds]));
        }
      }
    } else {
      newSelected = selectedIds.filter(sid => sid !== id);

      // If unselecting any permission, unselect the 'manage' permission of that group
      const groupName = perm.key.split('.')[0]?.toLowerCase();
      if (groupName && grouped[groupName]) {
        const managePerm = grouped[groupName].find(p =>
          p.key.split('.')[1]?.toLowerCase() === 'manage'
        );
        if (managePerm && id !== managePerm.id) {
          newSelected = newSelected.filter(sid => sid !== managePerm.id);
        }
      }
    }

    onChange(newSelected);
  };

  const toggleGroupAll = (groupPermissions) => {
    const groupIds = groupPermissions.map(p => p.id);
    const allSelected = groupIds.every(id => selectedIds.includes(id));

    if (allSelected) {
      // Deselect all in group
      onChange(selectedIds.filter(id => !groupIds.includes(id)));
    } else {
      // Select all in group
      onChange(Array.from(new Set([...selectedIds, ...groupIds])));
    }
  };

  const getActionLabel = (key) => {
    const parts = key.split('.');
    if (parts.length < 2) { return key; }

    const action = parts.slice(1).join('_');
    return action
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 bg-base-200/40 rounded-2xl border border-dashed border-base-content/10 space-y-3">
        <div className="w-10 h-10 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
        <p className="text-xs font-bold text-base-content/40 uppercase tracking-widest">
          Loading Permission Matrix...
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {orderedGroups.map(([entity, groupPermissions]) => {
          const config = ENTITY_CONFIG[entity] || {
            label: entity.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
            icon: Shield,
            color: 'text-primary',
          };
          const Icon = config.icon;
          const groupIds = groupPermissions.map(p => p.id);
          const selectedGroupCount = groupIds.filter(id => selectedIds.includes(id)).length;
          const allGroupSelected = groupPermissions.length > 0 && selectedGroupCount === groupPermissions.length;

          return (
            <div
              key={entity}
              className="glass-card rounded-2xl border border-base-content/10 shadow-xs overflow-hidden flex flex-col transition-all hover:border-base-content/20"
            >
              {/* Group Header */}
              <div className="px-4 py-3 border-b border-base-content/10 bg-base-200/50 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <Icon size={16} className={`flex-shrink-0 ${config.color || 'text-primary'}`} />
                  <h3 className="text-xs font-black text-base-content tracking-tight leading-tight break-words">
                    {config.label}
                  </h3>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <span className="text-[10px] font-bold text-base-content/40 font-mono">
                    {selectedGroupCount}/{groupPermissions.length}
                  </span>
                  <button
                    type="button"
                    onClick={() => toggleGroupAll(groupPermissions)}
                    className="text-[10px] font-bold text-primary hover:underline hover:text-primary-focus transition-colors"
                  >
                    {allGroupSelected ? 'None' : 'All'}
                  </button>
                </div>
              </div>

              {/* Group Checkboxes */}
              <div className="p-3 space-y-2 flex-1">
                {groupPermissions.map(p => {
                  const isSelected = selectedIds.includes(p.id);
                  return (
                    <label
                      key={p.id}
                      className="flex items-center gap-2.5 p-1.5 rounded-xl hover:bg-base-200/60 cursor-pointer select-none transition-colors group"
                    >
                      <div className="relative flex items-center justify-center flex-shrink-0">
                        <input
                          type="checkbox"
                          className="peer appearance-none w-4 h-4 rounded-md border border-base-300 bg-base-100 checked:bg-primary checked:border-primary transition-all cursor-pointer"
                          checked={isSelected}
                          onChange={() => togglePermission(p.id)}
                        />
                        <Check
                          size={11}
                          className="absolute text-primary-content opacity-0 peer-checked:opacity-100 transition-opacity pointer-events-none stroke-[3]"
                        />
                      </div>
                      <span
                        className={`text-xs font-semibold leading-tight transition-colors ${
                          isSelected ? 'text-base-content font-bold' : 'text-base-content/60 group-hover:text-base-content'
                        }`}
                      >
                        {getActionLabel(p.key)}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

PermissionGrid.propTypes = {
  permissions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      key: PropTypes.string.isRequired,
    })
  ).isRequired,
  selectedIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
  onChange: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

export default PermissionGrid;